import { ToastOptions } from "../../types/toast";

/** Extracts the server's error message, matching apiFetchNotOkHandler's own
 * field precedence, without consuming the response body a second time. */
export const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.clone().json();
    return body?.payload?.message ?? body?.message ?? body?.error ?? "";
  } catch {
    return "";
  }
};

/** True when the server rejected a write because the client's local copy of
 * the booking was stale (e.g. someone else updated it, or a diagnostic script
 * mutated it out of band) — a version conflict, not a genuine failure.
 * Requires "booking" alongside "out of date" so an unrelated 4xx that happens
 * to share that phrase (e.g. "Cache data out of date") isn't misclassified. */
export const isStaleBookingConflict = (message: string): boolean =>
  /\bbooking\b/i.test(message) && /\bout of date\b/i.test(message);

export const STALE_BOOKING_NOTIFICATION: ToastOptions = {
  heading: "This booking was updated elsewhere. Refresh and try again.",
  toastType: "warning"
};

const staleConflicts = new Set<string>();

/** Flags a rejected write so a caller that cannot see the response (writes go
 * through helpers that resolve to an unsaved booking rather than throwing) can
 * tell a version conflict apart from a genuine failure. */
export const markStaleBookingConflict = (bookingId: string): void => {
  staleConflicts.add(bookingId);
};

/** True once, for the write that was rejected as out of date. */
export const consumeStaleBookingConflict = (bookingId: string): boolean =>
  staleConflicts.delete(bookingId);

/**
 * Raised when the API rejects a write because the client's copy of the booking
 * is older than the stored one. Callers can retry after refetching the booking
 * so the write rebases onto the server's current version.
 */
export class StaleBookingConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StaleBookingConflictError";
  }
}
